import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { checkTicketAssignable, fetchOrderSuggestions } from './thunk';

/**
 * Custom hook for OrderAutocomplete logic.
 *
 * @param {Object} props - Components props needed for logic
 * @returns {Object} State and handler methods
 */
export const useThunk = ({ value, organisationId, warehouseId, setError, onAssignable }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [assignedTicket, setAssignedTicket] = useState(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Clear options and warnings when organisation or warehouse changes
  useEffect(() => {
    setOptions([]);
    setAssignedTicket(null);
    if (onAssignable) {
      onAssignable(null);
    }
  }, [organisationId, warehouseId]);

  const handleCheckAssignable = async (id) => {
    if (id && String(id).length >= 4) {
      const assignableResult = await checkTicketAssignable(id);
      if (assignableResult?.ticket) {
        setAssignedTicket(assignableResult.ticket);
        if (onAssignable) {
          onAssignable(assignableResult.ticket);
        }
      } else {
        setAssignedTicket(null);
        if (onAssignable) {
          onAssignable(null);
        }
      }
    } else {
      setAssignedTicket(null);
      if (onAssignable) {
        onAssignable(null);
      }
    }
  };

  const debouncedFetch = useCallback(
    debounce((term, orgId) => {
      if (term.length >= 4) {
        const params = { term };
        if (orgId) {
          params.organisation_id = orgId;
        }
        if (warehouseId) {
          params.warehouse_id = warehouseId;
        }

        setLoading(true);
        fetchOrderSuggestions(params)
          .then((res) => {
            setOptions(res.results || []);
          })
          .catch((err) => {
            console.error('Error fetching order suggestions:', err);
            const errorMessage = err?.response?.data?.error_message || 'Failed to fetch order suggestions.';
            if (setError) {
              setError(errorMessage);
            }
            setOptions([]);
          })
          .finally(() => setLoading(false));
      } else {
        setOptions([]);
      }
    }, 500),
    [setError, warehouseId],
  );

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    debouncedFetch(newInputValue, organisationId);
  };

  const handleBlur = () => {
    setOptions([]);
    setInputValue(value || '');
  };

  const assignedTicketLink = useMemo(() => {
    if (!!!assignedTicket?.id) {
      return '';
    }

    let category = assignedTicket?.category;

    if (category === 'archived') {
      category = 'archive';
    }

    return `/admin/support/${category}/${assignedTicket?.id}`;
  }, [assignedTicket?.category, assignedTicket?.id]);

  return {
    options,
    loading,
    inputValue,
    setInputValue,
    handleInputChange,
    handleBlur,
    assignedTicket,
    handleCheckAssignable,
    assignedTicketLink,
  };
};
